import axios from 'axios';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type PathSegment = string | number;

const CLIENT_FIELD_LABEL_KEYS = {
  nombre: 'pages.clientes.name',
  identificacion: 'pages.clientes.identificacion',
  telefonoPrincipal: 'pages.clientes.phone',
  telefonoReferencia: 'pages.clientes.phoneReference',
  email: 'pages.clientes.email',
  ip: 'pages.clientes.codigo',
  nit: 'pages.clientes.nit',
  direccion: 'pages.clientes.direccion',
  comentarios: 'pages.clientes.comments',
  diaCobro: 'pages.clientes.diaCobro',
  estadoCliente: 'pages.clientes.status',
  tipoCliente: 'pages.clientes.tipo',
  fechaNacimiento: 'pages.clientes.fechaNacimiento',
  fechaCobro: 'pages.clientes.fechaCobro',
  fechaAlta: 'pages.clientes.fechaAlta',
} as const;

type ClientField = keyof typeof CLIENT_FIELD_LABEL_KEYS;

interface StructuredValidationIssue {
  path: PathSegment[];
  message: string;
  validation?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPathSegment(value: unknown): value is PathSegment {
  return typeof value === 'string' || typeof value === 'number';
}

function isStructuredValidationIssue(value: unknown): value is StructuredValidationIssue {
  if (!isRecord(value) || typeof value.message !== 'string' || !Array.isArray(value.path)) {
    return false;
  }

  return value.path.every(isPathSegment);
}

function collectStructuredIssues(value: unknown): StructuredValidationIssue[] {
  if (Array.isArray(value)) {
    return value.flatMap(collectStructuredIssues);
  }

  if (!isRecord(value)) return [];
  if (isStructuredValidationIssue(value)) return [value];
  if ('issues' in value) return collectStructuredIssues(value.issues);

  return [];
}

function isClientField(value: string): value is ClientField {
  return Object.prototype.hasOwnProperty.call(CLIENT_FIELD_LABEL_KEYS, value);
}

function getIssueField(issue: StructuredValidationIssue): ClientField | null {
  const pathField = issue.path.find(
    (segment): segment is ClientField => typeof segment === 'string' && isClientField(segment),
  );
  if (pathField !== undefined) return pathField;
  if (issue.validation && isClientField(issue.validation)) return issue.validation;
  return null;
}

function getLocalizedIssueReason(issue: StructuredValidationIssue, translate: Translate): string {
  const field = getIssueField(issue);

  switch (field) {
    case 'email':
      return translate('pages.clientes.invalidEmail');
    case 'nombre':
      return translate('pages.clientes.nameRequired');
    case 'fechaCobro':
      return translate('pages.clientes.fechaCobroRequired');
    case 'estadoCliente':
      return translate('pages.clientes.statusRequired');
    default:
      return field
        ? translate('pages.clientes.validationField', {
            field: translate(CLIENT_FIELD_LABEL_KEYS[field]),
          })
        : translate('pages.clientes.validationFallback');
  }
}

/**
 * Server error codes of the billing-day-change adjustment, mapped to the
 * localized reasons the edit overlay surfaces to the operator.
 */
const CAMBIO_DIA_ERROR_KEYS: Record<string, string> = {
  CAMBIO_DIA_CICLO_DESACTUALIZADO: 'pages.clientes.ajusteDia.errorCiclo',
  CAMBIO_DIA_AJUSTE_NO_APLICABLE: 'pages.clientes.ajusteDia.errorNoAplicable',
  CAMBIO_DIA_SIN_PERMISO: 'pages.clientes.ajusteDia.errorPermiso',
  CAMBIO_DIA_OPERACION_KEY_CONFLICTO: 'pages.clientes.ajusteDia.errorOperacion',
  CAMBIO_DIA_PERIODO_INVALIDO: 'pages.clientes.ajusteDia.errorCalculo',
};

/** Localized reason for a failed billing-day-change adjustment, or null. */
export function getCambioDiaMutationErrorReason(
  error: unknown,
  translate: Translate,
): string | null {
  if (!axios.isAxiosError(error)) return null;

  const responseData: unknown = error.response?.data;
  if (!isRecord(responseData) || typeof responseData.error !== 'string') return null;

  const key = CAMBIO_DIA_ERROR_KEYS[responseData.error];
  return key ? translate(key) : null;
}

/** Extracts localized, public validation reasons without exposing server internals. */
export function getClienteMutationErrorReason(error: unknown, translate: Translate): string | null {
  if (!axios.isAxiosError(error)) return null;

  const status = error.response?.status;
  if (status === undefined || status < 400 || status >= 500) return null;

  const responseData: unknown = error.response?.data;
  if (!isRecord(responseData)) return null;

  const issues = [responseData.message, responseData.details, responseData.errors].flatMap(
    collectStructuredIssues,
  );
  const messages = issues
    .map((issue) => getLocalizedIssueReason(issue, translate))
    .filter((message, index, all) => all.indexOf(message) === index)
    .slice(0, 3);

  return messages.length > 0 ? messages.join('; ') : null;
}
